import { useArtwork, useArtworkForm, usePlacementPosition } from "@design-lab/entities/artwork";
import { type Side } from "@design-lab/shared";
import type { PlacementFormPosition } from "@design-lab/shared/types";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useTabNavigation } from "@design-lab/shared/hooks/useTabNavigation";
// FSD VIOLATION: Feature importing from widget. These components contain entity logic
// and cannot be moved to shared. Accepted as technical debt until architectural refactor.
// eslint-disable-next-line no-restricted-imports
import {
  DesignLabImageSelector,
  ImagePlacement,
  ImageSize
} from "@design-lab/widgets/design-lab-upload-artwork";

import { AddToCartToolbar } from "@design-lab/features/add-to-cart";
import { TabCard } from "@design-lab/shared/ui/layouts/TabCard";
import { PlacementControlTooltip } from "./PlacementControlTooltip";
import { SelectPrintQuality } from "./SelectPrintQuality";

export type DesignArtworkProps = {
  title: string;
  side: Side;
};

export function DesignArtwork({ side, title }: DesignArtworkProps) {
  const { lens } = useArtworkForm();
  // Anchor the lens to this component's own `side` prop (PIP-3131). The
  // form's `currentPlacement` field is updated by a post-commit effect in
  // useTab when the sub-tab changes, so on a sub-tab switch the newly-mounted
  // DesignArtwork briefly sees the previous side's placement and writes the
  // new side's image-derived dimensions into the wrong `.size` field.
  // Read unconditionally for Rules of Hooks; the value is only consumed in the
  // front branch below — for side="back" the read is harmless.
  const selectedFrontPlacementLens = lens.focus("selectedFrontPlacement");
  const { placement: selectedFrontPlacement } = usePlacementPosition(selectedFrontPlacementLens);
  // selectedFrontPlacement is typed PlacementFormPosition which includes
  // "back"; normalize to a front sub-position so a corrupted/restored value
  // can't make the front DesignArtwork write through the back lens. Matches
  // the convention in useDesignProjectSync.ts and useReviewPinningModal.ts.
  let placement: PlacementFormPosition;
  if (side === "back") {
    placement = "back";
  } else if (selectedFrontPlacement === "frontLeftChest") {
    placement = "frontLeftChest";
  } else {
    placement = "front";
  }

  const placementLens = lens.focus(placement);
  const { isDesktop } = useResponsive();
  const { getMachineBySide } = useArtwork();
  const machine = getMachineBySide(side);
  const { tab } = useTabNavigation();

  // Don't show footer on "design" tab - it should only show on "size" tab
  const shouldShowFooter = isDesktop && side === "back" && tab !== "design";

  let content;

  if (machine.state.matches("initial") || machine.state.hasTag("loading")) {
    content = (
      <>
        <DesignLabImageSelector side={side} />
      </>
    );
  } else {
    content = (
      <>
        {isDesktop ? (
          <>
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-semibold">Image size</h3>
              <ImageSize lens={placementLens.focus("size")} side={side} />
            </div>

            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center">
                <h3 className="font-semibold">Image placement</h3>
                <PlacementControlTooltip isDesktop={isDesktop} />
              </div>
              <ImagePlacement />
            </div>

            <SelectPrintQuality side={side} />
          </>
        ) : (
          <SelectPrintQuality side={side} />
        )}
      </>
    );
  }

  return (
    <TabCard
      title={title}
      showNextButton={!isDesktop}
      footer={shouldShowFooter && <AddToCartToolbar />}
    >
      {content}
    </TabCard>
  );
}
